import React, { useCallback, useEffect, useState } from "react"
import { Autocomplete, CircularProgress, Stack, TextField } from "@mui/material"
import { collection, getDocs, limit, query, where } from "firebase/firestore"

import { db } from "../../firebase"

type SponsorOption = { id: string; name: string }
type ServiceOption = { id: string; title: string; imageUrl?: string; disabled?: boolean }

// Services live at `sponsors/{sponsorId}/services/{serviceId}` with no cross-sponsor index
// (no collection-group query exists in this app — see StandaloneServicesComponent.tsx), so
// picking one is a two-step search: sponsor first (mirrors FeaturedBusinesses.tsx's
// SponsorSelectWidget), then that sponsor's services (mirrors RaffleSelectWidget.tsx's UI).
export const WheelServicePickerWidget: React.FC<{
  sponsorId: string | undefined
  serviceId: string | undefined
  onChange: (next: { sponsorId: string; serviceId: string; serviceTitle: string; imageUrl: string | null }) => void
}> = ({ sponsorId, serviceId, onChange }) => {
  const [sponsors, setSponsors] = useState<SponsorOption[]>([])
  const [sponsorSearch, setSponsorSearch] = useState("")
  const [loadingSponsors, setLoadingSponsors] = useState(false)

  const [services, setServices] = useState<ServiceOption[]>([])
  const [loadingServices, setLoadingServices] = useState(false)

  const loadSponsors = useCallback(async (term: string) => {
    setLoadingSponsors(true)
    try {
      const baseRef = collection(db, "sponsors")
      const q = term
        ? query(baseRef, where("name", ">=", term), where("name", "<=", `${term}`), limit(50))
        : query(baseRef, limit(50))
      const snap = await getDocs(q)
      setSponsors(
        snap.docs
          .map((docSnap) => ({ id: docSnap.id, name: String((docSnap.data() as any)?.name ?? docSnap.id) }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      )
    } catch (error) {
      console.error("Error loading sponsors:", error)
      setSponsors([])
    } finally {
      setLoadingSponsors(false)
    }
  }, [])

  useEffect(() => {
    loadSponsors("")
  }, [loadSponsors])

  useEffect(() => {
    if (!sponsorId) {
      setServices([])
      return
    }
    let cancelled = false
    setLoadingServices(true)
    getDocs(collection(db, `sponsors/${sponsorId}/services`))
      .then((snap) => {
        if (cancelled) return
        setServices(
          snap.docs.map((docSnap) => {
            const data = docSnap.data() as any
            return {
              id: docSnap.id,
              title: String(data?.title ?? data?.name ?? docSnap.id),
              imageUrl: data?.winBoxImageUrl || data?.iconUrl || data?.image || undefined,
              disabled: Boolean(data?.disabled),
            }
          }),
        )
      })
      .catch((error) => {
        console.error("Error loading services:", error)
        if (!cancelled) setServices([])
      })
      .finally(() => {
        if (!cancelled) setLoadingServices(false)
      })
    return () => {
      cancelled = true
    }
  }, [sponsorId])

  const selectedSponsor = sponsors.find((sponsor) => sponsor.id === sponsorId) ?? null
  const selectedService = services.find((service) => service.id === serviceId) ?? null

  return (
    <Stack spacing={2}>
      <Autocomplete<SponsorOption>
        options={sponsors}
        value={selectedSponsor}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => onChange({ sponsorId: value?.id ?? "", serviceId: "", serviceTitle: "", imageUrl: null })}
        inputValue={sponsorSearch}
        onInputChange={(_, value, reason) => {
          setSponsorSearch(value)
          if (reason === "input") {
            loadSponsors(value).catch(() => setLoadingSponsors(false))
          }
        }}
        getOptionLabel={(option) => option.name}
        loading={loadingSponsors}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Sponsor"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingSponsors ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <Autocomplete<ServiceOption>
        options={services}
        value={selectedService}
        disabled={!sponsorId}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) =>
          onChange({
            sponsorId: sponsorId ?? "",
            serviceId: value?.id ?? "",
            serviceTitle: value?.title ?? "",
            imageUrl: value?.imageUrl ?? null,
          })
        }
        getOptionLabel={(option) => option.title}
        loading={loadingServices}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Service"
            variant="outlined"
            fullWidth
            helperText={sponsorId ? "Only this sponsor's services are shown" : "Select a sponsor first"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingServices ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Stack>
  )
}

export default WheelServicePickerWidget
